import Cookies from 'js-cookie';
import axios from 'axios';
import qs from 'qs';
import { redirectToLogin } from './navigation-service';
import { clearAuthCookies, cookieOptions } from '@src/redux/authenticator';

export interface User {
  token: string;
  refreshToken: string;
  info: {
    email: string;
  };
}

export const getCurrentUser = (): User | null => {
  const token = Cookies.get('token');
  const refreshToken = Cookies.get('refreshToken');
  const email = Cookies.get('email');

  if (!token) return null;

  return {
    token,
    refreshToken: refreshToken || '',
    info: {
      email: email || '',
    },
  };
};

let refreshInFlight: Promise<string | null> | null = null;

const updateAuthCookies = (accessToken: string, newRefreshToken?: string) => {
  Cookies.set('token', accessToken, cookieOptions);
  if (newRefreshToken) {
    Cookies.set('refreshToken', newRefreshToken, cookieOptions);
  }
};

const refreshAccessToken = async (): Promise<string | null> => {
  if (refreshInFlight) return refreshInFlight;

  const doRefresh = async (): Promise<string | null> => {
    const refreshToken = Cookies.get('refreshToken');
    if (!refreshToken) return null;

    try {
      const dataConfig = qs.stringify({
        grant_type: 'refresh_token',
        refresh_token: refreshToken,
        client_id: import.meta.env.VITE_COGNITO_SAML_POOL_CLIENT_ID,
      });

      const tokenUrl = `${import.meta.env.VITE_COGNITO_URL_DOMAIN}/oauth2/token`;
      const resp = await axios.post(tokenUrl, dataConfig, {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      });

      const newAccessToken = resp.data?.access_token as string | undefined;
      const newRefreshToken = resp.data?.refresh_token as string | undefined;

      if (!newAccessToken) return null;

      updateAuthCookies(newAccessToken, newRefreshToken);
      return newAccessToken;
    } catch {
      return null;
    }
  };

  refreshInFlight = doRefresh().finally(() => {
    refreshInFlight = null;
  });

  return refreshInFlight;
};

export const getAuthToken = async (): Promise<string | null> => {
  const token = Cookies.get('token');
  const refreshToken = Cookies.get('refreshToken');

  if (!token) {
    if (refreshToken) {
      const refreshed = await refreshAccessToken();
      if (refreshed) return refreshed;
    }
    clearAuthCookies();
    redirectToLogin();
    return null;
  }

  try {
    const payloadBase64 = token.split('.')[1];
    const payloadJson = atob(payloadBase64);
    const payload = JSON.parse(payloadJson);

    const currentTime = Math.floor(Date.now() / 1000);
    const expirationTime = payload.exp;
    const bufferTime = 60;

    if (currentTime > expirationTime - bufferTime) {
      const refreshed = await refreshAccessToken();
      if (refreshed) return refreshed;

      clearAuthCookies();
      redirectToLogin();
      return null;
    }

    return token;
  } catch {
    const refreshed = await refreshAccessToken();
    if (refreshed) return refreshed;

    clearAuthCookies();
    redirectToLogin();
    return null;
  }
};
